import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	Input,
	ChangeDetectorRef,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	AfterViewInit,
	OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal, NgbDatepickerModule, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { CreatedShare } from '@meta-press/shared-plugins';
import { environment } from '../../../environments/environment';

@Component({
	selector: 'app-export-notification-modal',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbDatepickerModule],
	templateUrl: './export-notification-modal.component.html',
	styleUrl: './export-notification-modal.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportNotificationModalComponent implements OnInit, AfterViewInit {
	@Input() isExporting = false;
	@Input() isPublishing = false; // Whether we're waiting for publish (share creation) to complete
	@Input() exportCompleted = false;
	@Input() exportError = false;
	@Input() showNamePrompt = true;
	@Input() exportId?: number; // The ID of the saved export
	@Input() hasShares = false; // Whether this export has any shares (default false to show publish prompt for new exports)
	@Input() createdShare?: CreatedShare; // The created share after successful publish
	@Output() nameSubmitted = new EventEmitter<string>();
	@Output() publishRequested = new EventEmitter<number>(); // Emitted when user clicks "Julkaise..." (deprecated)
	@Output() publishWithExpiry = new EventEmitter<number>(); // Emitted with expiry timestamp when user wants to publish immediately
	@ViewChild('nameInput') nameInput!: ElementRef<HTMLInputElement>;

	exportName = '';
	validationErrors: string[] = [];
	showValidationErrors = false; // Hide validation errors until first user input

	// Publish options
	shouldPublish = false; // Whether user wants to publish the game
	hasExpiryDate = false; // Whether user wants to set an expiry date
	expirationDatePicker: NgbDateStruct | null = null; // Date picker model
	expirationHour = 23; // Default to end of day
	expirationMinute = 59; // Default to end of day
	expirationDateTime = -1; // Final Unix timestamp for expiry (-1 means no expiry)

	constructor(
		public activeModal: NgbActiveModal,
		private router: Router,
		private cdr: ChangeDetectorRef,
		private modalService: NgbModal,
	) {}

	ngOnInit(): void {
		// Auto-sanitize the export name if it was provided as input
		if (this.exportName) {
			const originalName = this.exportName;
			// Remove file extension from the export name (e.g., ".mpx", ".json")
			this.exportName = this.removeFileExtension(this.exportName);
			this.exportName = this.sanitizeFileName(this.exportName);

			// If name was changed or if it's still invalid, validate to check
			this.validateFileName();

			// If there are validation errors after sanitization, show them immediately
			if (this.validationErrors.length > 0) {
				this.showValidationErrors = true;
			}

			this.cdr.detectChanges();
		}
	}

	ngAfterViewInit(): void {
		// Focus the name input field after the view is initialized
		setTimeout(() => {
			if (this.showNamePrompt && this.nameInput) {
				this.nameInput.nativeElement.focus();
				this.cdr.detectChanges();
			}
		}, 300);
	}

	// Method to manually update state and trigger change detection
	updateState(
		isExporting: boolean,
		exportCompleted: boolean,
		exportError: boolean,
	): void {
		this.isExporting = isExporting;
		this.exportCompleted = exportCompleted;
		this.exportError = exportError;
		this.showNamePrompt = false;
		// Manually trigger change detection
		this.cdr.detectChanges();
	}

	/**
	 * Remove file extension from filename (e.g., ".mpx", ".json", etc.)
	 * @param filename The filename to process
	 * @returns Filename without extension
	 */
	private removeFileExtension(filename: string): string {
		// Find the last dot in the filename
		const lastDotIndex = filename.lastIndexOf('.');

		// If there's a dot and it's not at the beginning (hidden files like .gitignore)
		if (lastDotIndex > 0) {
			return filename.substring(0, lastDotIndex);
		}

		// Return original filename if no extension found
		return filename;
	}

	/**
	 * Sanitize title name by removing invalid characters
	 * Note: This is for a title field, not a filename
	 * @param name - The title name to sanitize
	 * @returns Sanitized title name
	 */
	sanitizeFileName(name: string): string {
		// Trim whitespace from both ends
		let sanitized = name.trim();

		// Remove forbidden characters: < > " / \ | ? *
		// Note: Colon (:) is allowed as this is a title, not a filename
		const forbiddenChars = /[<>"\/\\|?*]/g;
		sanitized = sanitized.replace(forbiddenChars, '');

		return sanitized;
	}

	// Comprehensive validation for title names
	validateFileName(): void {
		this.validationErrors = [];
		const trimmedName = this.exportName.trim();

		// Check if name is empty or only whitespace
		if (!trimmedName) {
			this.validationErrors.push('Tallennuksen nimi ei voi olla tyhjä');
			return;
		}

		// Check minimum length (at least 1 meaningful character)
		if (trimmedName.length < 1) {
			this.validationErrors.push('Tallennuksen nimi on liian lyhyt');
			return;
		}

		// Optional: Check for maximum length to prevent overly long names
		if (trimmedName.length > 100) {
			this.validationErrors.push(
				'Tallennuksen nimi on liian pitkä (maksimi 100 merkkiä)',
			);
		}

		// Check for forbidden characters (note: colon is allowed as this is a title)
		const forbiddenChars = /[<>"\/\\|?*]/;
		if (forbiddenChars.test(trimmedName)) {
			this.validationErrors.push(
				'Tallennuksen nimessä ei saa olla merkkejä: < > " / \\ | ? *',
			);
		}
	}

	// Check if current name is valid
	isNameValid(): boolean {
		this.validateFileName();
		return this.validationErrors.length === 0;
	}

	// Handle input changes with real-time validation
	onNameChange(): void {
		// Enable error display on first user input
		this.showValidationErrors = true;
		this.validateFileName();
		this.cdr.detectChanges();
	}

	submitExportName(): void {
		// Use comprehensive validation instead of simple trim check
		if (this.isNameValid()) {
			this.showNamePrompt = false;

			// If user wants to publish, set isPublishing immediately to show the correct message
			if (this.shouldPublish) {
				this.isPublishing = true;
			}
			this.isExporting = true;

			this.nameSubmitted.emit(this.exportName.trim());

			// If user wants to publish, emit the publish event with expiry timestamp
			if (this.shouldPublish) {
				this.publishWithExpiry.emit(this.expirationDateTime);
			}

			this.cdr.detectChanges();
		} else {
			// Trigger validation to show error messages
			this.validateFileName();
			this.cdr.detectChanges();
		}
	}

	/**
	 * Handle publish checkbox change
	 */
	onPublishCheckboxChange(): void {
		// If unchecking, also uncheck expiry date
		if (!this.shouldPublish) {
			this.hasExpiryDate = false;
			this.expirationDateTime = -1;
		}
		this.cdr.detectChanges();
	}

	/**
	 * Handle expiry date checkbox change
	 */
	onExpiryDateCheckboxChange(): void {
		if (this.hasExpiryDate) {
			// Set default date to tomorrow
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);
			this.expirationDatePicker = {
				year: tomorrow.getFullYear(),
				month: tomorrow.getMonth() + 1,
				day: tomorrow.getDate()
			};
			this.updateExpirationDateTime();
		} else {
			// No expiry
			this.expirationDateTime = -1;
		}
		this.cdr.detectChanges();
	}

	/**
	 * Update expiration date/time Unix timestamp from date picker and time inputs
	 */
	updateExpirationDateTime(): void {
		if (!this.hasExpiryDate || !this.expirationDatePicker) {
			this.expirationDateTime = -1;
			return;
		}

		// Create date object from date picker
		const date = new Date(
			this.expirationDatePicker.year,
			this.expirationDatePicker.month - 1,
			this.expirationDatePicker.day,
			this.expirationHour || 0,
			this.expirationMinute || 0,
			0,
			0
		);

		// Convert to Unix timestamp (seconds)
		this.expirationDateTime = Math.floor(date.getTime() / 1000);
	}

	closeModal(): void {
		this.activeModal.close();
	}

	goToExports(): void {
		this.modalService.dismissAll();
		this.router.navigate(['/exports']);
	}

	/**
	 * Open the share modal for publishing the export
	 */
	openShareModal(): void {
		if (this.exportId !== undefined) {
			this.publishRequested.emit(this.exportId);
		} else {
			console.error('Cannot open share modal: exportId is undefined');
		}
	}

	/**
	 * Get share URL from UUID
	 */
	GetShareURL(uuid: string): string {
		const currentUrl = window.location.href;
		const urlBase = new URL('..', currentUrl).href;
		// Remove trailing slash if present
		const baseUrlWithoutTrailingSlash = urlBase.endsWith('/')
			? urlBase.slice(0, -1)
			: urlBase;

		const dataVersion = environment.appDataVersion;
		return `${baseUrlWithoutTrailingSlash}/viewers/${dataVersion}/#/?uuid=${uuid}`;
	}

	/**
	 * Get share iframe embed code
	 */
	GetShareIFrameCode(uuid: string): string {
		const link = this.GetShareURL(uuid);
		const iframeCode = `<iframe src="${link}" width="100%" height="100%" frameborder="0" allowfullscreen></iframe>`;
		return iframeCode;
	}

	/**
	 * Copy text to clipboard
	 */
	copyToClipboard(text: string): void {
		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(text)
				.then(() => {
					// Success toast would be shown here, but NotificationService is not injected
					console.log('Copied to clipboard:', text);
				})
				.catch((err: unknown) => {
					console.error('Failed to copy:', err);
				});
		} else {
			// Fallback for older browsers
			const textArea = document.createElement('textarea');
			textArea.value = text;
			textArea.style.position = 'fixed';
			textArea.style.left = '0';
			textArea.style.top = '0';
			textArea.style.opacity = '0';
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			try {
				document.execCommand('copy');
				console.log('Copied to clipboard (fallback):', text);
			} catch (err) {
				console.error('Fallback: Unable to copy', err);
			}
			document.body.removeChild(textArea);
		}
	}
}
